import {BehaviorSubject, bindCallback, combineLatestWith, firstValueFrom, of, Subject, switchMap} from 'rxjs';
import {filter, tap} from 'rxjs/operators';
import {userApi} from '@/api';
import {loadTelegramWidget} from '@/resource/js';
import {LANG_MAP} from '@/map';
import {doCaptcha} from '@/captcha';
import {sendRequest} from '@/modules/bonus/utils';
import {navAfterRegisterSuccess} from '@/helper/login';

interface TelegramLoinState {
  ready: boolean;
  loading: boolean;
}

export function loginTelegram(target, referralCode, $lang$, LG) {
  const state$ = new BehaviorSubject({ready: false, loading: false});
  const botInfo$ = new BehaviorSubject<Record<string, any>>({});

  function init() {
    const value = state$.getValue();
    return of('')
      .pipe(
        switchMap(() => userApi.telegramBotInfo({source: 'telegram'})),
        tap((r) => {
          if (r.success) {
            botInfo$.next(r.data);
          }
        }),
        switchMap(() => loadTelegramWidget()),
        tap(() => {
          state$.next({...value, ready: true});
        })
      )
      .subscribe(() => {});
  }

  function handleClick() {
    const value = state$.getValue();
    return of(value)
      .pipe(
        filter((s) => s.ready && !s.loading),
        tap((s) => {
          state$.next({...s, loading: true});
        }),
        combineLatestWith(botInfo$),
        switchMap(([_, botInfo]) => {
          return of(bindCallback(window.Telegram.Login.auth)).pipe(
            switchMap((s) => {
              return firstValueFrom(s({bot_id: botInfo?.id, request_access: 'write', embed: 1}));
            })
          );
        }),
        switchMap((data: Record<string, any>) => {
          if (data) {
            return login(data);
          }
          return '';
        }),
        tap(() => {
          state$.next({...value, loading: false});
        })
      )
      .subscribe(() => {});
  }

  async function login(user) {
    user.id = `${user.id}`;
    user.auth_date = `${user.auth_date}`;
    const params = {
      authSource: 'telegram',
      lang: LANG_MAP[$lang$] || $lang$,
      referralCode,
      authParam: JSON.stringify(user),
      userDeviceLang: (navigator.language || navigator.userLanguage).substring(0, 2),
    };
    const captchaResult = await doCaptcha('register');
    const {data, error} = await sendRequest(userApi.authLogin(captchaResult, params), LG);
    if (data?.decision === 1 || data?.decision === 3) {
      const params = {email: data.email, code: data.code};
      const {data: data2} = await sendRequest(userApi.confirmAuthLogin(params), LG);
      if (data2) {
        navAfterRegisterSuccess(target);
      }
    }
  }

  return {
    state$,
    init,
    handleClick,
  };
}
